import { Modal } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { estadosEvento, hotelesOpciones } from "../../../hook/Arreglos";
import {} from "../../../store/clientes/thunkClientes";
import { NavLink, useNavigate } from "react-router-dom";

/**
 * Componente ModalMostrarInfo
 * Este modal muestra la información de un evento específico y sus eventos relacionados.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {boolean} props.open - Estado de apertura del modal.
 * @param {Function} props.handleClose - Función para cerrar el modal.
 * @param {Function} props.funciones - Función adicional para manejar eventos en el modal (opcional).
 * @param {string} props.type - Tipo de modal (opcional).
 * @param {Object} props.datas - Datos proporcionados al abrir el modal, incluyendo información del evento.
 * @returns {JSX.Element} Un modal con información del evento.
 */
export const ModalMostrarInfo = ({ open, handleClose, funciones, type, datas }) => {
    const navigate = useNavigate();
    // Hook para manejar acciones de Redux.
    const dispatch = useDispatch();

    // Estados locales para manejar información de eventos, salones y cliente.
    const [informacion, setInformacion] = useState(null); // Almacena la información principal del evento.
    const [eventosRelacionados, setEventosRelacionados] = useState([]); // Almacena eventos relacionados.
    const [salones, setSalones] = useState([]); // Almacena la lista de salones disponibles.
    const [infoCliente, setIdCliente] = useState(null); // Almacena información del cliente asociado.

    /**
     * Función asíncrona para obtener información detallada de un evento.
     * Se asegura de cargar dinámicamente los thunks necesarios y actualizar el estado local.
     *
     * @param {number} idEvento - ID del evento a consultar.
     */
    const FetchInformacion = useCallback(
        async (idEvento) => {
            // Carga dinámica de thunks para optimizar el rendimiento al no importarlos innecesariamente.
            const { obtenerEventoEspesifico } = await import("../../../store/Calendario/thunkCalendario");
            const { obtenerListaSalones } = await import("../../../store/Calendario/thunkCalendario");
            const { getSpecificLead } = await import("../../../store/clientes/thunkClientes");

            try {
                // Validación inicial para evitar llamadas innecesarias si el ID no es válido.
                if (!idEvento) return;

                // Llama al thunk para obtener los detalles del evento desde el backend.
                const response = await dispatch(obtenerEventoEspesifico(idEvento));

                // Maneja los datos del evento principal.
                if (response.eventoPrincipal.length === 0) {
                    setInformacion([]); // Si no hay datos, inicializa como arreglo vacío.
                } else {
                    setInformacion(response.eventoPrincipal); // Actualiza con los datos obtenidos.
                }

                // Maneja los eventos relacionados.
                if (response.eventosRelacionados.length === 0) {
                    setEventosRelacionados([]); // Si no hay datos relacionados, inicializa como vacío.
                } else {
                    setEventosRelacionados(response.eventosRelacionados); // Actualiza la lista.
                }

                // Obtiene la lista de salones basados en el hotel asociado al evento.
                const responseSalones = await dispatch(obtenerListaSalones(response.eventoPrincipal.id_hotel));
                setSalones(responseSalones);

                // Si existe un cliente asociado, obtiene sus datos.
                if (response.eventoPrincipal.id_cleinteev > 0) {
                    const leadData = await dispatch(getSpecificLead(response.eventoPrincipal.id_cleinteev));
                    setIdCliente(leadData);
                } else {
                    setIdCliente(null);
                }
            } catch (error) {
                // Manejo de errores: registra cualquier problema ocurrido durante la ejecución.
                console.error("Error al obtener la información del evento:", error);
            }
        },
        [dispatch], // `dispatch` como dependencia para evitar recreaciones innecesarias.
    );

    /**
     * Efecto secundario para cargar automáticamente los datos del evento al abrir el modal.
     * Se ejecuta cuando `datas` o el ID del evento cambian.
     */
    useEffect(() => {
        if (datas?.event?.extendedProps?._id) {
            FetchInformacion(datas.event.extendedProps._id); // Llama a la función para cargar la información.
        }
    }, [FetchInformacion, datas?.event?.extendedProps?._id]);

    /**
     * Obtiene el label (nombre) asociado a un hotel dado su ID.
     *
     * @param {string|number} idHotel - ID del hotel.
     * @param {Array<Object>} opciones - Lista de opciones con propiedades `value` (ID) y `label` (nombre).
     * @returns {string} - Devuelve el nombre del hotel o "No encontrado" si no hay coincidencia.
     */
    const obtenerLabelHotel = (idHotel, opciones) => {
        const hotel = opciones.find((opcion) => opcion.value === idHotel.toString());
        return hotel ? hotel.label : "No encontrado";
    };

    /**
     * Obtiene el nombre del salón asociado a un ID específico.
     *
     * @param {string|number} idSalon - ID del salón.
     * @returns {string} - Devuelve el nombre del salón o "No encontrado" si no hay coincidencia.
     */
    const obtenerLabelSalon = (idSalon) => {
        const salon = salones.find((opcion) => opcion.id_salon === parseInt(idSalon, 10));
        return salon ? salon.nombre_salon : "No encontrado";
    };

    /**
     * Obtiene el nombre del estado asociado a un evento por su ID.
     *
     * @param {string|number} eventoId - ID del estado del evento.
     * @returns {string} - Nombre del estado o "No encontrado" si no coincide.
     */
    const estadoEvento = (eventoId) => {
        const estado = estadosEvento.find((opcion) => opcion.id_estado === parseInt(eventoId, 10));
        return estado ? estado.nombre_estado : "No encontrado";
    };

    /**
     * Navega a la vista del evento utilizando el ID del evento actual.
     *
     * @param {number} id - ID del evento.
     */
    const irEvento = (id) => {
        navigate(`/evento/copia?data=${datas?.event?.extendedProps?._id}`);
    };

    /**
     * Navega a la página de edición de un evento.
     *
     * @param {number} id - ID del evento.
     */
    const editar = (id) => {
        navigate(`/evento/ver?data=${datas?.event?.extendedProps?._id}`);
    };

    /**
     * Genera una cotización basada en los datos del evento actual.
     * Navega a la página de creación de cotización con parámetros específicos.
     */
    const generarCotizacion = () => {
        navigate(`/cotizaciones/crear?data=${informacion.id_referencia}`);
    };

    return (
        <Modal
            open={open} // Controla si el modal está abierto o cerrado.
            onClose={handleClose} // Llama a la función para cerrar el modal.
            className="modal fade show" // Clase personalizada para estilos adicionales.
            style={{ display: "block" }} // Forzar el estilo para que el modal sea visible.
        >
            <div className="modal-dialog" style={{ maxWidth: "60%", margin: "1.75rem auto" }}>
                <div className="modal-content">
                    <div className="modal-body">
                        {informacion ? (
                            <>
                                <p>Acciones de Generar copia o Generar Cotización</p>
                                <div style={{ display: "flex", gap: "10px" }}>
                                    <button type="button" className="btn-shadow btn btn-danger" onClick={() => editar()}>
                                        Editar evento
                                    </button>
                                    <button type="button" className="btn-shadow btn btn-success" onClick={() => irEvento()}>
                                        Generar Copia
                                    </button>
                                    <button type="button" className="btn-shadow btn btn-warning" onClick={() => generarCotizacion()}>
                                        Generar Cotización
                                    </button>
                                </div>
                                <br />
                                <div className="card">
                                    <div className="card-header">
                                        <h4>Nombre del evento: {informacion?.nombre_evento} </h4>
                                    </div>
                                    <div className="card-body">
                                        <ul className="list-group list-group-flush">
                                            <li className="list-group-item px-0 pt-0">
                                                <div className="row">
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Nombre del evento</p>
                                                        <p className="mb-0">
                                                            <NavLink to={`/evento/ver?data=${informacion.id_evento}`} className="pc-link active">
                                                                {informacion?.nombre_evento}
                                                            </NavLink>
                                                        </p>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Tipo de evento</p>
                                                        <p className="mb-0">{informacion?.tipo_evento}</p>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Ejecutivos</p>
                                                        <p className="mb-0">{informacion.nombres_admins}</p>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Hotel Asignado</p>
                                                        <p className="mb-0">{obtenerLabelHotel(informacion.id_hotel, hotelesOpciones)}</p>
                                                    </div>
                                                </div>
                                            </li>
                                            <br />
                                            <li className="list-group-item px-0 pt-0">
                                                <div className="row">
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Salon Asignado</p>
                                                        <p className="mb-0">{obtenerLabelSalon(informacion.id_salon)}</p>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Fecha inicio Evento</p>
                                                        <p className="mb-0">{informacion.fech_Inicio_evento}</p>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Fecha Final Evento</p>
                                                        <p className="mb-0">{informacion.fech_final_evento}</p>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Cantidad Persona</p>
                                                        <p className="mb-0">{informacion.cantidad_evento}</p>
                                                    </div>
                                                </div>
                                            </li>
                                            <br />
                                            <li className="list-group-item px-0 pt-0">
                                                <div className="row">
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Ingreso esperado</p>
                                                        <p className="mb-0">{informacion.ingresos_evento}</p>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Montaje</p>
                                                        <p className="mb-0">{informacion.montaje}</p>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Estado del Evento</p>
                                                        <p className="mb-0">{estadoEvento(informacion.estado_evento)}</p>
                                                    </div>
                                                    <div className="col-md-3">
                                                        <p className="mb-1 text-muted">Notas de evento</p>
                                                        <p className="mb-0">{informacion.notas_evento}</p>
                                                    </div>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                {infoCliente ? (
                                    (
                                    (
                                        <>
                                            <div className="card">
                                                <div className="card-header">
                                                    <h5>Cliente Relacioando</h5>
                                                </div>
                                                <div className="card-body">
                                                    <ul className="list-group list-group-flush">
                                                        <li className="list-group-item px-0 pt-0">
                                                            <div className="row">
                                                                <div className="col-md-3">
                                                                    <p className="mb-1 text-muted">Nombre Cliente</p>
                                                                    <p className="mb-0">
                                                                        <NavLink to={`/clientes/perfil?data=${infoCliente.id_cliente}`} className="pc-link active">
                                                                            {infoCliente.nombre_cliente}
                                                                        </NavLink>
                                                                    </p>
                                                                </div>
                                                                <div className="col-md-3">
                                                                    <p className="mb-1 text-muted">Correo Cliente</p>
                                                                    <p className="mb-0">{infoCliente.correo_cleinte}</p>
                                                                </div>
                                                                <div className="col-md-3">
                                                                    <p className="mb-1 text-muted">Telefono Cliente</p>
                                                                    <p className="mb-0">{infoCliente.telefono_cliente}</p>
                                                                </div>
                                                                <div className="col-md-3">
                                                                    <p className="mb-1 text-muted">Empresa Cliente</p>
                                                                    <p className="mb-0">
                                                                        <NavLink to={`/empresas/perfil?data=${infoCliente.idEmpresaRelacion}`} className="pc-link active">
                                                                            {infoCliente.nombre_empresa}
                                                                        </NavLink>
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </>
                                    ))
                                ) : (
                                    <p></p>
                                )}

                                <div className="card" style={{ backgroundColor: informacion.color_evento }}>
                                    <div className="card-body">
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1 me-3">
                                                <h3 className="text-white">{informacion.nombre_cliente}</h3>
                                            </div>
                                            <div className="flex-shrink-0">
                                                <img alt="img" loading="lazy" width="92" height="90" decoding="async" data-nimg="1" className="img-fluid wid-80" srcSet="" src="https://light-able-react-light.vercel.app/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fimg-accout-alert.a2294f08.png&w=96&q=75" style={{ color: "transparent" }} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        {eventosRelacionados.length > 0 ? (
                                            eventosRelacionados.map((entry, idx) => {
                                                return (
                                                    <div className="col-xl-4 col-sm-6" key={idx} onClick={() => FetchInformacion(entry.id_evento)}>
                                                        <div className="statistics-card-1 card">
                                                            <div className="card-header d-flex align-items-center justify-content-between py-3">
                                                                <h5>
                                                                    <NavLink to={`/evento/ver?data=${entry.id_evento}`} className="pc-link active">
                                                                        {entry.nombre_evento}
                                                                    </NavLink>
                                                                </h5>
                                                            </div>

                                                            <div className="card-body">
                                                                <img alt="img" width="63" height="134" decoding="async" data-nimg="1" className="img-fluid img-bg" src="https://light-able-react-light.vercel.app/_next/static/media/img-status-2.4d72f177.svg" style={{ color: "transparent" }} />
                                                                <li className="list-group-item px-0 pt-0">
                                                                    <div className="row">
                                                                        <div className="col-md-6">
                                                                            <p className="mb-1 text-muted">Salon Asignado</p>
                                                                            <p className="mb-0">{obtenerLabelSalon(entry.id_salon)}</p>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <p className="mb-1 text-muted">Fecha inicio Evento</p>
                                                                            <p className="mb-0">{entry.fech_Inicio_evento}</p>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                                <li className="list-group-item px-0 pt-0">
                                                                    <div className="row">
                                                                        <div className="col-md-6">
                                                                            <p className="mb-1 text-muted">Hotel Asignado</p>
                                                                            <p className="mb-0">{obtenerLabelHotel(entry.id_hotel, hotelesOpciones)}</p>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <p className="mb-1 text-muted">Fecha Final Evento</p>
                                                                            <p className="mb-0">{entry.fech_final_evento}</p>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <p></p>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <p></p> // Muestra un mensaje mientras se carga la información.
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};
